import * as request from './api-request'
import { currentRouteFeature } from './feature-from-route'
import { currentRouteUuids } from './paywall-route-uuids'

const ENDPOINT = 'analytics/paywall'
const FLUSH_INTERVAL_MS = 5000
const MAX_BUFFER = 20
const SESSION_STORAGE_KEY = 'paywallAnalyticsSessionId'
const TOKEN_STORAGE_KEY = 'token'

let buffer = []
let timer = null

// Identifies the user's *login*, not the tab and not the browser profile:
// shared by every tab open under the same login, stable across reloads, and
// replaced the next time someone signs in. That is why this lives in
// `localStorage` (shared across tabs, survives reload) rather than
// `sessionStorage` (per tab, which used to let a logout/login in one tab
// keep the previous login's id, and gave two tabs of the same login two
// different ids).
//
// Minting is no longer done from a read: `getSessionId` used to lazily
// create an id on first read, which is exactly how a signed-out visitor and
// the next signed-in user could end up sharing one. Only
// `startAnalyticsSession`/`endAnalyticsSession`, driven by the Firebase auth
// observer, may create or remove the id now -- see firebase-context.js.
//
// The whole body of every function here is guarded, not just a `typeof`
// check: when a browser is configured to block storage access, *reading*
// the `localStorage` global throws SecurityError rather than returning
// undefined, and `typeof` invokes that getter. `setItem`/`removeItem` can
// throw on quota or the same block too. These run inside a useEffect in
// UpgradePlanBody/BannerItem and inside the auth observer, and an uncaught
// throw there unmounts the tree under React 18 -- analytics is never
// allowed to break a render.
export const getSessionId = () => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof localStorage === 'undefined') return 'no-session'
    // eslint-disable-next-line no-undef
    return localStorage.getItem(SESSION_STORAGE_KEY) || 'no-session'
  } catch (error) {
    return 'no-session'
  }
}

// Mints an id only if one is not already stored, so calling this on every
// sign-in (including a token refresh that re-fires the observer with the
// same user) does not rotate the id mid-login.
export const startAnalyticsSession = () => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof localStorage === 'undefined') return
    // eslint-disable-next-line no-undef
    if (localStorage.getItem(SESSION_STORAGE_KEY)) return
    const created = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    // eslint-disable-next-line no-undef
    localStorage.setItem(SESSION_STORAGE_KEY, created)
  } catch (error) {
    // Analytics must never break a render.
  }
}

// Called from the auth observer whenever Firebase reports signed-out, so the
// next login mints a fresh id rather than inheriting this one.
export const endAnalyticsSession = () => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof localStorage === 'undefined') return
    // eslint-disable-next-line no-undef
    localStorage.removeItem(SESSION_STORAGE_KEY)
  } catch (error) {
    // Analytics must never break a render.
  }
}

const currentPath = () => {
  // eslint-disable-next-line no-undef
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line no-undef
  return window.location?.pathname || null
}

// The token get-token last wrote, read synchronously. The unload path cannot
// await getToken(): that is a Firebase round-trip, and the navigation that
// triggered the flush aborts it long before it resolves.
const cachedToken = () => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof localStorage === 'undefined') return null
    // eslint-disable-next-line no-undef
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch (error) {
    return null
  }
}

// #1437: project/script uuids used to only exist in `path`, which
// paywall-validation.js strips them out of on purpose (see sanitisePath) so
// the column stays low-cardinality and groupable. Resolving them here --
// same place, same reasoning as featureName below -- puts them in `props`
// instead, where they are available for drill-down without touching path's
// cardinality. Every event gets this (impression and every cta click alike),
// same as featureName/path, so a call site never has to know about it.
const withRouteUuidProps = (props) => {
  const { projectUuid, scriptUuid } = currentRouteUuids()
  if (!projectUuid && !scriptUuid) return props
  return {
    ...props,
    ...(projectUuid ? { project_uuid: projectUuid } : {}),
    ...(scriptUuid ? { script_uuid: scriptUuid } : {})
  }
}

// Resolved here rather than at each call site so all ~80 places that open the
// upgrade modal are instrumented without being touched, and so the value comes
// from the same resolver ai_usage attribution uses -- which is what lets the
// two tables join on feature_name.
export const trackPaywallEvent = (event) => {
  buffer.push({
    ...event,
    featureName: event.featureName || currentRouteFeature(),
    sessionId: getSessionId(),
    // Correlates one impression with every cta click from the same mount --
    // minted once per mount by usePaywallTracking and passed straight
    // through here, never derived. `...event` already carries it, but this
    // is kept explicit so it survives a future rewrite of this spread.
    eventId: event.eventId || null,
    path: currentPath(),
    props: withRouteUuidProps(event.props),
    occurredAt: new Date().toISOString()
  })

  if (buffer.length >= MAX_BUFFER) {
    flushPaywallEvents()
    return
  }
  if (!timer) {
    // eslint-disable-next-line no-undef
    timer = setTimeout(flushPaywallEvents, FLUSH_INTERVAL_MS)
  }
}

const clearFlushTimer = () => {
  if (timer) {
    // eslint-disable-next-line no-undef
    clearTimeout(timer)
    timer = null
  }
}

// Always resolves. Analytics is not allowed to surface an error to the user or
// reject into a render path, so a failed flush is dropped rather than retried
// forever -- a retry loop against a failing API would be worse than the gap.
export const flushPaywallEvents = async () => {
  clearFlushTimer()
  if (buffer.length === 0) return
  const events = buffer
  buffer = []

  try {
    await request.toJSON('POST', ENDPOINT, { events })
  } catch (error) {
    // Swallowed on purpose. See above.
  }
}

// The unload-safe variant. `navigator.sendBeacon` is the usual answer here and
// is deliberately NOT used: it cannot set an Authorization header, and this
// endpoint is behind withAuth. The only way to authenticate a beacon would be
// `?token=<firebase id token>` on the URL, which puts a bearer credential into
// every access log and proxy between here and the API -- not a trade worth
// making for an analytics row.
//
// `fetch(..., { keepalive: true })` is what sendBeacon is built on and does
// carry headers, so it survives the document being torn down while still
// authenticating properly. It needs the request built synchronously, hence the
// cached token above.
const sendKeepalive = (events) => {
  const token = cachedToken()
  if (!token) return false
  try {
    // eslint-disable-next-line no-undef
    if (typeof fetch !== 'function') return false
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    }
    const workspaceUuid = request.getStoredWorkspaceUuid()
    if (workspaceUuid) {
      headers['x-workspace-id'] = workspaceUuid
    }
    // eslint-disable-next-line no-undef
    const pending = fetch(request.getEndpointPath(ENDPOINT), {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify({ events })
    })
    if (pending && typeof pending.catch === 'function') pending.catch(() => {})
    return true
  } catch (error) {
    return false
  }
}

// Called when the document is going away: `pagehide`, and `visibilitychange`
// to hidden. Both matter -- a tab navigating to Stripe fires pagehide, and a
// backgrounded mobile tab that is later discarded only ever fires
// visibilitychange. Without this the 5s timer loses every event belonging to a
// click that navigates, which is exactly the click worth measuring.
export const flushPaywallEventsOnUnload = () => {
  clearFlushTimer()
  if (buffer.length === 0) return
  const events = buffer
  buffer = []
  if (sendKeepalive(events)) return
  // No cached token, or no fetch at all: put the events back and take the
  // ordinary awaited path. It may not outlive the document, but dropping them
  // on the floor here is strictly worse.
  buffer = events.concat(buffer)
  flushPaywallEvents()
}

// eslint-disable-next-line no-undef
if (typeof document !== 'undefined') {
  // eslint-disable-next-line no-undef
  document.addEventListener('visibilitychange', () => {
    // eslint-disable-next-line no-undef
    if (document.visibilityState === 'hidden') flushPaywallEventsOnUnload()
  })
}

// eslint-disable-next-line no-undef
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-undef
  window.addEventListener('pagehide', flushPaywallEventsOnUnload)
}

export const __resetForTests = () => {
  buffer = []
  clearFlushTimer()
}

// Records that a persistent surface has already produced an impression in this
// login session, and reports whether this caller is the one that claimed it.
// Returns true exactly once per (session, surface, key).
//
// Scoped to the analytics session id, so it resets on logout/login along with
// everything else, and keyed per surface+card so two different banner cards do
// not collapse into one. Stored rather than held in memory because a full page
// load must not re-count -- that is the noise this exists to remove.
export const claimSessionImpression = (surface, dedupeKey = null) => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof localStorage === 'undefined') return true
    const storageKey = `paywallImpressionSeen:${getSessionId()}:${surface}:${dedupeKey || '-'}`
    // eslint-disable-next-line no-undef
    if (localStorage.getItem(storageKey)) return false
    // eslint-disable-next-line no-undef
    localStorage.setItem(storageKey, '1')
    return true
  } catch (error) {
    // Storage blocked: fall back to recording the impression. Over-counting is
    // the lesser fault -- silently dropping it would understate reach.
    return true
  }
}
